import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Report, Ticket } from "../../../types";
import { fetchTicketsByStatus, fetchTicketsByStatusBatch, getAllColumnNames, updateItemColumn, updateLostItemDate, updateLostItemStatus } from "./columnsAsyncThunk";
import { LostStatuses } from "../../../types/LostStatuses";
import { ReportType } from "./columnTypes";
import { toast } from "react-toastify";
import { FoundReport } from "../../../types/FoundItemTypes";
import { ColumnName } from "./columnTypes";

export interface Filters {
  dateField?: string;
  fromDate?: string;
  toDate?: string;
  searchField?: string;
  searchValue?: string;
  status?: number;
  establishmentType?: string | null;
}
interface ColumnState {
  tickets: Record<number, Ticket[]>; // Tickets grouped by status
  loading: Record<number, boolean>;
  counts: Record<number, number>;
  offsets: Record<number, number>;
  hasMore: Record<number, boolean>;
  openedColumn: number | undefined;
  openedColumnType: ReportType | undefined;
  initReportType: ReportType;
  filters: Filters;
  deleteReason: string;
  deletionSource: "" | "manual" | "auto-unpaid" | "auto-draft";
  shipFilter: boolean | null;
  columnNames: ColumnName[];
  columnLoading: boolean;
  boardStatusFilter: number | "notPaid" | "drafts" | "unpaid" | null;
  paymentStatusFilter: "paid" | "notPaid";
  filterEpoch: number;
}

const initialState: ColumnState = {
  tickets: {},
  loading: {},
  counts: {},
  offsets: {},
  hasMore: {},
  openedColumn: undefined,
  openedColumnType: undefined,
  initReportType: ReportType.Lost,
  filters: { searchField: "lost.trackingId", searchValue: "" },
  deleteReason: "",
  deletionSource: "",
  shipFilter: null,
  columnNames: [],
  columnLoading: false,
  boardStatusFilter: null,
  paymentStatusFilter: "paid",
  filterEpoch: 0,
};

const getTicketUniqueId = (ticket: Ticket) => {
  return ticket?.id || ticket?.trackingId || `${ticket?.status}-${ticket?.createdAt || ""}`;
};

const getUrgentSortValue = (ticket: Ticket) => {
  const sourceDate = ticket?.statusUpdatedAt || ticket?.createdAt;
  return sourceDate ? new Date(sourceDate).getTime() : 0;
};

const syncUrgentTickets = (state: ColumnState) => {
  const urgentMap = new Map<string, Ticket>();

  Object.keys(state.tickets).forEach((statusKey) => {
    const columnStatus = Number(statusKey);
    if (Number.isNaN(columnStatus)) return;
    const bucket = state.tickets[columnStatus] || [];
    bucket?.forEach((ticket) => {
      if (!ticket) return;
      if (ticket.isUrgent || ticket.status === LostStatuses.Urgent) {
        const key = getTicketUniqueId(ticket);
        if (key) {
          urgentMap.set(key, ticket);
        }
      }
    });
  });

  const urgentList = Array.from(urgentMap.values()).sort((ticketA, ticketB) => {
    const timeDifference = getUrgentSortValue(ticketB) - getUrgentSortValue(ticketA);
    if (timeDifference !== 0) return timeDifference;
    const idA = getTicketUniqueId(ticketA);
    const idB = getTicketUniqueId(ticketB);
    return idA.localeCompare(idB);
  });

  state.tickets[LostStatuses.Urgent] = urgentList;
  state.counts[LostStatuses.Urgent] = urgentList.length;
  state.offsets[LostStatuses.Urgent] = urgentList.length;
  state.hasMore[LostStatuses.Urgent] = false;
  state.loading[LostStatuses.Urgent] = false;
};

const columnsSlice = createSlice({
  name: "columns",
  initialState,
  reducers: {
    //set filter
    mergeFilters: (state, action: PayloadAction<ColumnState["filters"]>) => {
      state.tickets = {};
      state.loading = {};
      state.offsets = {};
      state.counts = {};
      state.hasMore = {};
      state.filters = { ...state.filters, ...action.payload };
      // Bump epoch so any in-flight request resolves into a no-op
      state.filterEpoch = state.filterEpoch + 1;
      // Intentionally NOT calling syncUrgentTickets here — state.tickets is empty, so
      // sync would only set tickets[Urgent] = [], which makes the fetch effect think
      // Urgent is already loaded and skip it. Leaving Urgent undefined ensures the next
      // batch fetch refetches Urgent directly from the backend.
    },
    updateTicketLocally: (state, action: PayloadAction<{ ticket: Ticket; type?: ReportType }>) => {
      const { ticket, type = ReportType.Lost } = action.payload;
      const selectedTicket = state.tickets[ticket?.status]?.find((t) => t.id === ticket.id);
      if (ticket.isUrgent) {
        const selectedTicketInUrgent = state.tickets[LostStatuses.Urgent]?.find((t) => t.id === ticket.id);
        if (selectedTicketInUrgent) {
          state.tickets[LostStatuses.Urgent] = state.tickets[LostStatuses.Urgent].map((t) => (t.id === ticket.id ? { ...t, ...ticket } : t));
        } else {
          state.tickets[LostStatuses.Urgent] = [ticket, ...(state.tickets[LostStatuses.Urgent] || [])];
        }
      } else {
        state.tickets[LostStatuses.Urgent] = state.tickets[LostStatuses.Urgent]?.filter((t) => t.id !== ticket.id) || [];
      }
      if (selectedTicket) {
        if (selectedTicket.status === ticket.status) {
          state.tickets[ticket.status] = state.tickets[ticket.status].map((t) => (t.id === ticket.id ? { ...t, ...ticket } : t));
          syncUrgentTickets(state);
          return;
        }
        state.tickets[ticket.status] = state.tickets[ticket.status].map((t) => (t.id === ticket.id ? { ...t, ...ticket } : t));
        state.tickets[selectedTicket.status] = state.tickets[selectedTicket.status].filter((t) => t.id !== selectedTicket.id);
      } else {
        const ticketStatus = type === ReportType.Lost ? ticket.status : LostStatuses.LFNotified;
        const stateTickets = state.tickets[ticketStatus] || [];
        const findTicket = stateTickets?.find((t) => t.id === ticket.id);
        if (findTicket?.id) {
          state.tickets[ticketStatus] = state.tickets[ticketStatus].map((t) => (t.id === ticket.id ? { ...t, ...ticket } : t));
        } else {
          state.tickets[ticketStatus] = [ticket, ...stateTickets];
          state.counts[ticketStatus] = state.counts[ticketStatus] + 1;
          state.offsets[ticketStatus] = state.offsets[ticketStatus] + 1;
          state.hasMore[ticketStatus] = !(state.tickets[ticketStatus].length === state.counts[ticketStatus]);
        }
      }
      syncUrgentTickets(state);
    },
    // Update ticket status locally
    updateTicketStatusLocally: (state, action: PayloadAction<{ ticket: Ticket; sourceStatus: number; targetStatus: number }>) => {
      const { ticket, sourceStatus, targetStatus } = action.payload;

      // Remove ticket from source status
      const selectedTicket = state.tickets[sourceStatus].find((t) => t.id === ticket.id);
      if (selectedTicket) {
        state.tickets[sourceStatus] = state.tickets[sourceStatus].filter((t) => t.id !== ticket.id);
        state.counts[sourceStatus] = state.counts[sourceStatus] - 1;
        state.offsets[sourceStatus] = state.offsets[sourceStatus] - 1;

        // Add ticket to target status
        if (!state.tickets[targetStatus]) state.tickets[targetStatus] = [];
        state.tickets[targetStatus] = [{ ...ticket, status: targetStatus }, ...state.tickets[targetStatus]];
        state.counts[targetStatus] = state.counts[targetStatus] + 1;
        state.offsets[targetStatus] = state.offsets[targetStatus] + 1;
      }
      syncUrgentTickets(state);
    },

    // Revert ticket status on API failure`
    revertTicketStatus: (state, action: PayloadAction<{ ticket: Ticket; sourceStatus: number; targetStatus: number }>) => {
      const { ticket, sourceStatus, targetStatus } = action.payload;

      // Remove ticket from target status
      state.tickets[targetStatus] = state.tickets[targetStatus].filter((t) => t.id !== ticket.id);
      state.counts[sourceStatus] = state.counts[sourceStatus] + 1;
      state.offsets[sourceStatus] = state.offsets[sourceStatus] + 1;

      // Add ticket back to source status
      if (!state.tickets[sourceStatus]) state.tickets[sourceStatus] = [];
      state.tickets[sourceStatus] = [{ ...ticket, status: sourceStatus }, ...state.tickets[sourceStatus]];
      state.counts[targetStatus] = state.counts[targetStatus] - 1;
      state.offsets[targetStatus] = state.offsets[targetStatus] - 1;
      syncUrgentTickets(state);
    },
    setOpenColumn: (state, action: PayloadAction<number | undefined>) => {
      state.openedColumn = action.payload;
    },
    setOpenedColumnType: (state, action: PayloadAction<ReportType | undefined>) => {
      state.openedColumnType = action.payload;
    },
    setReportType: (state, action: PayloadAction<ReportType>) => {
      state.initReportType = action.payload;
    },
    clearTicketsByStatus: (state, action: PayloadAction<number>) => {
      delete state.tickets[action.payload];
      state.loading[action.payload] = false;
      state.counts[action.payload] = 0;
      state.offsets[action.payload] = 0;
      state.hasMore[action.payload] = false;
      // Skip syncUrgentTickets when clearing Urgent — it would immediately
      // re-create columns[6] from other columns, preventing a fresh backend fetch
      if (action.payload !== LostStatuses.Urgent) {
        syncUrgentTickets(state);
      }
    },
    removeTicketById: (state, action: PayloadAction<{ id: string }>) => {
      const { id } = action.payload;
      Object.keys(state.tickets).forEach((statusKey) => {
        const status = Number(statusKey);
        const index = state.tickets[status].findIndex((t: Ticket) => t?.id === id);
        if (index !== -1) {
          state.tickets[status].splice(index, 1);
          state.counts[status] = state.counts[status] - 1;
          state.offsets[status] = state.offsets[status] - 1;
        }
      });
      syncUrgentTickets(state);
    },
    setDeleteReason: (state, action: PayloadAction<string>) => {
      state.tickets = {};
      state.loading = {};
      state.offsets = {};
      state.counts = {};
      state.hasMore = {};
      state.shipFilter = null;
      state.deleteReason = action.payload;
      // Skip syncUrgentTickets after full clear — see mergeFilters for the rationale.
    },
    setDeletionSource: (state, action: PayloadAction<"" | "manual" | "auto-unpaid" | "auto-draft">) => {
      state.tickets = {};
      state.loading = {};
      state.offsets = {};
      state.counts = {};
      state.hasMore = {};
      state.deletionSource = action.payload;
      state.filterEpoch = state.filterEpoch + 1;
      // Skip syncUrgentTickets after full clear — see mergeFilters for the rationale.
    },
    setShipFilter: (state, action: PayloadAction<boolean | null>) => {
      state.tickets = {};
      state.loading = {};
      state.offsets = {};
      state.counts = {};
      state.hasMore = {};
      state.deleteReason = "";
      state.shipFilter = action.payload;
      // Skip syncUrgentTickets after full clear — see mergeFilters for the rationale.
    },
    setBoardStatusFilter: (state, action: PayloadAction<number | "notPaid" | "drafts" | "unpaid" | null>) => {
      state.boardStatusFilter = action.payload;
    },
    setPaymentStatusFilter: (state, action: PayloadAction<"paid" | "notPaid">) => {
      state.tickets = {};
      state.loading = {};
      state.offsets = {};
      state.counts = {};
      state.hasMore = {};
      state.paymentStatusFilter = action.payload;
      state.filterEpoch = state.filterEpoch + 1;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTicketsByStatus.pending, (state, action) => {
        const { status } = action.meta.arg;
        state.loading[status] = true;
      })
      .addCase(fetchTicketsByStatus.fulfilled, (state, action) => {
        const { status, tickets, offset, counts, epoch } = action.payload as any;
        // Drop stale responses if the user changed filters mid-flight
        if (typeof epoch === "number" && epoch !== state.filterEpoch) {
          state.loading[status] = false;
          return;
        }
        state.tickets[status] = [...(state.tickets[status] || []), ...tickets];
        state.offsets[status] = offset + tickets?.length;
        // Backend returns -1 for load-more pages — retain existing count
        if (counts !== -1 && counts !== undefined) {
          state.counts[status] = counts;
        }
        state.loading[status] = false;
        const effectiveCount = state.counts[status] ?? 0;
        state.hasMore[status] = !(state.tickets[status].length >= effectiveCount);
        syncUrgentTickets(state);
      })
      .addCase(fetchTicketsByStatus.rejected, (state, action) => {
        const { status } = action.meta.arg;
        state.loading[status] = false;
        // Ensure the column is marked as initialized even on error
        // so the board doesn't show infinite loading
        if (!state.tickets[status]) {
          state.tickets[status] = [];
        }
      })
      .addCase(fetchTicketsByStatusBatch.pending, (state, action) => {
        const { columns } = action.meta.arg;
        columns.forEach((col) => { state.loading[col.status] = true; });
      })
      .addCase(fetchTicketsByStatusBatch.fulfilled, (state, action) => {
        const { results, epoch } = action.payload as any;
        // Drop stale batch responses if filters changed since the request was issued
        if (typeof epoch === "number" && epoch !== state.filterEpoch) {
          results.forEach((r: any) => { state.loading[r.status] = false; });
          return;
        }
        results.forEach((r: any) => {
          state.tickets[r.status] = [...(state.tickets[r.status] || []), ...r.tickets];
          state.offsets[r.status] = r.offset + (r.tickets?.length || 0);
          // -1 signals "retain existing count" (backend skips count on load-more)
          if (r.counts !== -1 && r.counts !== undefined) {
            state.counts[r.status] = r.counts;
          }
          state.loading[r.status] = false;
          const effectiveCount = state.counts[r.status] ?? 0;
          state.hasMore[r.status] = !(state.tickets[r.status].length >= effectiveCount);
        });
        syncUrgentTickets(state);
      })
      .addCase(fetchTicketsByStatusBatch.rejected, (state, action) => {
        const { columns } = action.meta.arg;
        columns.forEach((col) => {
          state.loading[col.status] = false;
          if (!state.tickets[col.status]) state.tickets[col.status] = [];
        });
      })
      .addCase(updateLostItemStatus.fulfilled, (state, action) => {
        // const { id, status } = action.payload;
        // No-op: status is already updated locally in `updateTicketStatusLocally`
      })
      .addCase(updateLostItemStatus.rejected, (state, action: any) => {
        // No direct state changes here, handled in the caller
        toast.error(action?.payload);
      })
      .addCase(updateLostItemDate.fulfilled, (state, action) => {
        // No-op: status is already updated locally in `updateTicketStatusLocally`
        toast.success("Item lost date updated successfully");
      })
      .addCase(updateLostItemDate.rejected, (state, action: any) => {
        // No direct state changes here, handled in the caller
        toast.error(action?.payload);
      })
      .addCase(getAllColumnNames.fulfilled, (state, action) => {
        state.columnNames = action.payload;
      })
      .addCase(getAllColumnNames.rejected, (state, action) => {
        console.log(action);
      })
      .addCase(updateItemColumn.pending, (state, action) => {
        state.columnLoading = true;
      })
      .addCase(updateItemColumn.fulfilled, (state, action) => {
        state.columnNames = state.columnNames.map((column) =>
          column.id === action.payload.id ? { ...column, name: action.payload.name } : column
        );
        state.columnLoading = false;
      })
      .addCase(updateItemColumn.rejected, (state, action) => {
        state.columnLoading = false;
      })
  },
});

export const { updateTicketStatusLocally, updateTicketLocally, revertTicketStatus, setOpenColumn, mergeFilters, setReportType, setOpenedColumnType, clearTicketsByStatus, removeTicketById, setDeleteReason, setDeletionSource, setShipFilter, setBoardStatusFilter, setPaymentStatusFilter } =
  columnsSlice.actions;
export default columnsSlice.reducer;

export const convertIntoBoardTicket = (ticket: Report | FoundReport | any): Ticket => {
  return {
    id: ticket.id,
    trackingId: ticket.trackingId,
    status: ticket.status,
    lostDate: ticket.lostDate,
    firstName: ticket.user?.firstName || "NA",
    lastName: ticket.user?.lastName || "NA",
    categoryName: ticket.category?.name,
    subCategoryName: ticket.subCategory?.name,
    createdAt: ticket.createdAt,
    statusUpdatedAt: ticket.statusUpdatedAt,
    documents:
      ticket?.documents?.map((doc: any) => ({
        ...doc,
        id: String(doc.id),
      })) || [],
    isUrgent: ticket?.isUrgent || false,
    isOverdue: ticket?.isOverdue || false,
    overdueAt: ticket?.overdueAt,
    foundBy: ticket?.foundBy || "",
    foundDate: ticket?.foundDate || "",
    isPendingRequest: ticket?.isPendingRequest || false,
    requestType: ticket?.requestType,
    itemNumber: ticket?.itemNumber,
    caseManagerName: ticket?.caseManagerName || "",
    isPaymentSuccess: ticket.isPaymentSuccess || false,
    isDraft: ticket.isDraft || false,
    shippingOptions: ticket.shippingOptions || [],
    hasShippingProof: ticket.hasShippingProof || false,
    deliveryDate: ticket.deliveryDate || "",
    establishmentTypeIcon: ticket.establishmentTypeIcon || "",
    establishmentType: ticket.establishmentType || "",
    countryCode: ticket.countryCode || ticket.user?.countryCode || ""
  };
};
